import { useState } from "react";
import { toast } from "sonner";
import { TeamMemberData } from "../types";

export const useBandingActions = (
  currentUser: any,
  teamMembersData: TeamMemberData[],
  setTeamMembersData: (data: TeamMemberData[]) => void,
  saveBandingToDatabase: (data: TeamMemberData[]) => Promise<any>
) => {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [isReleasedBanding, setIsReleasedBanding] = useState(false);

  // Helper function to check if a status should lock the band value
  const isStatusLocked = (status: string | null | undefined): boolean => {
    return status === 'Final Band Accepted' || 
           status === 'Band disagreement submitted' || 
           status === 'Band disagreement resolution completed';
  };

  const handleSave = async () => {
    if (!currentUser || isSavingAll) return;

    setIsSavingAll(true);
    console.log('useBandingActions: Starting save all operation');

    try {
      const result = await saveBandingToDatabase(teamMembersData);
      
      if (result.success) {
        setDialogMessage("Performance banding saved successfully!");
        setShowSuccessDialog(true);
        setIsReleasedBanding(true);
        console.log('useBandingActions: Save all completed successfully');
      } else {
        setDialogMessage(`Error saving performance banding: ${result.error}`);
        setShowErrorDialog(true);
        console.error('useBandingActions: Save all failed:', result.error);
      }
    } catch (error) {
      console.error('useBandingActions: Error in handleSave:', error);
      setDialogMessage(`Error saving performance banding: ${error}`);
      setShowErrorDialog(true);
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleRowSave = async (member: TeamMemberData) => {
    if (!currentUser) return;

    console.log(`useBandingActions: Saving individual row for ${member.full_name} with status: "${member.status}"`);

    try {
      const result = await saveBandingToDatabase([member]);
      
      if (result.success) {
        toast.success(`Performance band saved for ${member.full_name}`);
        console.log(`useBandingActions: Row save completed for ${member.full_name}`);
      } else {
        toast.error(`Error saving performance band for ${member.full_name}`);
        console.error(`useBandingActions: Row save failed for ${member.full_name}:`, result.error);
      }
    } catch (error) {
      console.error(`useBandingActions: Error in handleRowSave for ${member.full_name}:`, error);
      toast.error(`Error saving performance band for ${member.full_name}`);
    }
  };

  const handleRowEdit = (memberId: string) => {
    console.log(`useBandingActions: Editing row for member ID: ${memberId}`);
    setTeamMembersData(
      teamMembersData.map(member =>
        member.id === memberId
          ? { ...member, isCommentEditable: true }
          : member
      )
    );
  };

  const handleCommentsChange = (memberId: string, comments: string) => {
    console.log(`useBandingActions: Updating comments for member ID: ${memberId}`);
    setTeamMembersData(
      teamMembersData.map(member =>
        member.id === memberId
          ? { 
              ...member, 
              supervisorBandingComments: comments,
              // CRITICAL: NEVER modify status when updating comments
              // Keep the existing status unchanged
            }
          : member
      )
    );
  };

  return {
    showSuccessDialog,
    setShowSuccessDialog,
    showErrorDialog,
    setShowErrorDialog,
    dialogMessage,
    isSavingAll,
    isReleasedBanding,
    handleSave,
    handleRowSave,
    handleRowEdit,
    handleCommentsChange
  };
};
